// src/oura/tasks/TasksView.tsx
//
// Delivery > Tasks: the client's Master Tracker, row for row (Jack, 23 Oct
// 2026). Phase tabs as in the sheet, the same columns, the same five
// statuses. A change here goes to the Google Sheet and a change in the sheet
// shows here, within 2 minutes. The plan lines (S1, G1...) roll up from the
// tasks that start with their code.

import { useEffect, useMemo, useState } from "react";
import { ExternalLinkIcon, PencilIcon, PlusIcon, RefreshCwIcon, SheetIcon, Trash2Icon, TriangleAlertIcon } from "lucide-react";
import type { NewTask, TaskChange } from "@service/aeo/tasks";
import { cn } from "../lib/cn";
import { agoText } from "../docs/format";
import { Drawer, DrawerBlock, DrawerHead, PillButton, Seg, Tag, TYPE, type Tone } from "../ui";
import { TASK_STATUSES, actionText, codesOf, isoDay, ownerKind, phaseCount, planLines, sheetDay, type Task, type TaskHistory, type TaskPhase } from "./model";

const STATUS_TONE: Record<string, Tone> = {
    "Not started": "gray",
    "In progress": "blue",
    Blocked: "rose",
    "Awaiting client": "amber",
    Done: "green",
};

type OwnerFilter = "all" | "us" | "client";

/** What a change made in Oura came back as: the saved task, or the server's sentence (and the sheet's value when the sheet won). */
export type ChangeAnswer = { ok: true; task: Task } | { ok: false; message: string; field?: string; sheetValue?: string };
/** A new task as the screen sends it: the tab is the phase it was added under. */
export type AddAnswer = { ok: true; task: Task } | { ok: false; message: string; code?: string; field?: string };
/** A delete: gone, or refused. On sheet_won the task is the sheet's version, and it stays. */
export type DeleteAnswer = { ok: true } | { ok: false; message: string; code?: string; task?: Task };

/** A write that did not reach the sheet: shown in the banner above the table as well as where it was made. */
const isWriteFailure = (code?: string) => code === "write_failed" || code === "write_unconfirmed";
const OWNERS = ["ourteam", "Client", "ourteam + Client"];

/** The longest Result record the server takes (ourteam-backend#6692, `result_too_long`). */
const RESULT_MAX = 5000;
/** One plan code or several ("G1", "G1, G2", "G1-G2"): sent as `code`, written "G1-G2" in the sheet. Else a label. */
const CODES_RE = /^[A-Za-z]\d{1,2}(?:[\s,;-]+[A-Za-z]\d{1,2})*$/;
/** The "Needs a code" card, hidden for now (2 Nov 2026). Its rows still save a code or label; flip to bring it back. */
const NEEDS_CODE_ON = false;
const FIELD_WORD: Record<string, string> = { status: "Status", result: "Result record", target: "Target", owner: "Owner", notes: "Notes", action: "Action", label: "Label", row: "Row" };

export interface TasksViewProps {
    phases: TaskPhase[];
    sheetUrl: string;
    /** "2 min ago": when Oura last read the sheet. */
    syncedAgo: string;
    /** Rows last changed in the sheet, by id: "Angel, 2 min ago". */
    changedInSheet?: Record<string, string>;
    /** The phase open first. */
    initialPhase?: string;
    initialOpen?: string | null;
    /** Sample rows, not the client's sheet: says so instead of claiming a sync. */
    sample?: boolean;
    /** Live: save a change to Oura and the sheet. Without it, changes stay on screen (sample). */
    onChange?: (id: string, change: TaskChange) => Promise<ChangeAnswer>;
    /** Live, behind TASKS_WRITE_ROWS: add a row to the tab named `tab` (the phase's name). Without it, no Add button. */
    onAdd?: (tab: string, body: Omit<NewTask, "tab">) => Promise<AddAnswer>;
    /** Live, behind TASKS_WRITE_ROWS: delete a task's row in the sheet and in Oura. Without it, no Delete in the panel. */
    onDelete?: (id: string) => Promise<DeleteAnswer>;
    /** Live: read the sheet now; answers an error sentence, or null. */
    onReadNow?: () => Promise<string | null>;
    /** The last problem reading the sheet ("Row 12 of M2: unknown status 'done?'"). */
    lastError?: string | null;
    /** Ids of rows with no plan code and no label. */
    needsCode?: string[];
}

export function TasksView({
    phases: start,
    sheetUrl,
    syncedAgo,
    changedInSheet = {},
    initialPhase,
    initialOpen = null,
    sample = false,
    onChange,
    onAdd,
    onDelete,
    onReadNow,
    lastError = null,
    needsCode = [],
}: TasksViewProps) {
    const [phases, setPhases] = useState(start);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [busyId, setBusyId] = useState<string | null>(null);
    const [reading, setReading] = useState(false);
    const [readError, setReadError] = useState<string | null>(null);
    const [writeAlert, setWriteAlert] = useState<string | null>(null);
    const [append, setAppend] = useState("");
    // The Result record being rewritten (#4815): null when it is only shown.
    const [rewrite, setRewrite] = useState<string | null>(null);
    // The plan code (or a label) typed in the drawer, kept with the task it was typed for: a save that
    // ends after another task opened clears only its own task's draft, never the new one (Jack).
    const [codeDraft, setCodeDraft] = useState<{ id: string | null; text: string }>({ id: null, text: "" });
    const [confirmClear, setConfirmClear] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [phaseKey, setPhaseKey] = useState(initialPhase ?? start[0]?.key ?? "P0");
    const [owner, setOwner] = useState<OwnerFilter>("all");
    const [openId, setOpenId] = useState<string | null>(initialOpen);
    // A draft belongs to the task it was typed for: another task opens with an empty one. The delete
    // confirm and the refusals of the last visit do not carry over to the next task opened.
    useEffect(() => {
        setAppend("");
        setCodeDraft({ id: openId, text: "" });
        setRewrite(null);
        setConfirmClear(false);
        setConfirmDelete(false);
        if (openId) setErrors((e) => ({ ...e, [`${openId}:target`]: "", [`${openId}:delete`]: "", [`${openId}:result`]: "", [`${openId}:code`]: "" }));
    }, [openId]);
    const phase = phases.find((p) => p.key === phaseKey) ?? phases[0];
    const rows = useMemo(() => phase.tasks.filter((t) => owner === "all" || (owner === "client" ? ownerKind(t.owner) !== "us" : ownerKind(t.owner) !== "client")), [phase, owner]);
    const open = openId ? (phases.flatMap((p) => p.tasks).find((t) => t.id === openId) ?? null) : null;
    const openPhase = open ? phases.find((p) => p.tasks.some((t) => t.id === open.id)) : null;

    const update = (id: string, patch: Partial<Task>) =>
        setPhases((ps) =>
            ps.map((p) => ({
                ...p,
                tasks: p.tasks.map((t) => (t.id === id ? { ...t, ...patch } : t)),
            })),
        );

    /**
     * A change: saved through `onChange` when live (the answer replaces the row), kept on screen in a sample.
     * `errKey` is where a refusal is shown: the row by default, "<id>:target" for the Target field in the panel.
     */
    const change = async (id: string, ch: TaskChange, errKey = id): Promise<boolean> => {
        if (!onChange) {
            update(id, { ...(ch.status ? { status: ch.status } : {}), ...(ch.target ? { target: sheetDay(ch.target) } : {}) });
            return true;
        }
        setBusyId(id);
        setErrors((e) => ({ ...e, [errKey]: "" }));
        const ans = await onChange(id, ch);
        setBusyId(null);
        if (ans.ok) {
            update(id, ans.task);
            return true;
        }
        setErrors((e) => ({ ...e, [errKey]: ans.message }));
        // The sheet won: show its value, which is the task's now.
        if (ans.field === "status" && ans.sheetValue) update(id, { status: ans.sheetValue });
        if (ans.field === "target" && ans.sheetValue !== undefined) update(id, { target: ans.sheetValue });
        if (ans.field === "result" && ans.sheetValue !== undefined) {
            update(id, { result: ans.sheetValue });
            // A rewrite in progress starts again from the sheet's text, so staff can re-apply their change to it.
            setRewrite((r) => (r === null ? r : ans.sheetValue ?? ""));
        }
        return false;
    };

    const readNow = async () => {
        if (!onReadNow) return;
        setReading(true);
        setReadError(null);
        try {
            const error = await onReadNow();
            setReadError(error);
            // A read that worked settles a write_unconfirmed, so the old write banner goes with it.
            // A failed one settles nothing, and the banner stays.
            if (error === null) setWriteAlert(null);
        } finally {
            setReading(false);
        }
    };

    /** Add a row under a phase. The table grows only once the server has answered 201; nothing is added before that. */
    const addRow = async (phase: TaskPhase, body: Omit<NewTask, "tab">): Promise<AddAnswer> => {
        if (!onAdd) return { ok: false, message: "Adding a task is not on for this client." };
        setWriteAlert(null);
        const ans = await onAdd(phase.name, body);
        if (ans.ok) setPhases((ps) => ps.map((p) => (p.key === phase.key ? { ...p, tasks: [...p.tasks, ans.task] } : p)));
        else if (isWriteFailure(ans.code)) setWriteAlert(ans.message);
        return ans;
    };

    /** Delete a task's row. Nothing leaves the table before the server answers 200. */
    const remove = async (id: string) => {
        if (!onDelete) return;
        setBusyId(id);
        setWriteAlert(null);
        setErrors((e) => ({ ...e, [`${id}:delete`]: "" }));
        const ans = await onDelete(id);
        setBusyId(null);
        if (ans.ok) {
            setPhases((ps) => ps.map((p) => ({ ...p, tasks: p.tasks.filter((t) => t.id !== id) })));
            // Staff may have opened another task while this one was deleting; leave that one open.
            setOpenId((current) => (current === id ? null : current));
            return;
        }
        // The sheet won: the row stays, takes the sheet's version, and the panel stays open to look again.
        if (ans.task) update(id, ans.task);
        setConfirmDelete(false);
        setErrors((e) => ({ ...e, [`${id}:delete`]: ans.message }));
        if (isWriteFailure(ans.code)) setWriteAlert(ans.message);
    };

    const all = phases.flatMap((p) => p.tasks);
    const done = all.filter((t) => t.status === "Done").length;
    const uncoded = all.filter((t) => needsCode.includes(t.id) && !t.label && !(t.codes ?? []).length);
    const alertText = writeAlert ?? readError ?? lastError;

    return (
        <div className="space-y-4">
            <div className="flex flex-col gap-3 rounded-2xl bg-white px-4 py-3 ring-1 ring-inset ring-black/[0.06] sm:flex-row sm:items-center">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-green-50 text-green-700">
                    <SheetIcon size={15} />
                </span>
                <p className={cn(TYPE.tb, "m-0 min-w-0 flex-1 text-gray-900")}>
                    {sample ? "Sample tasks" : "In sync with the Google Sheet"}{" "}
                    <span className="text-gray-500">
                        · {sample ? "" : `read ${syncedAgo} · `}
                        {done} of {all.length} tasks done
                    </span>
                    <span className={cn(TYPE.tc, "block text-gray-500")}>
                        {sample
                            ? "Preview only. These are not this client's tasks, and changes are not saved to Oura or the Google Sheet."
                            : "Changes here go to the sheet, and changes in the sheet show here, within 2 minutes."}
                    </span>
                </p>
                {!sample && onReadNow && (
                    <PillButton icon={RefreshCwIcon} busy={reading} onClick={readNow}>
                        Read now
                    </PillButton>
                )}
                {!sample && (
                    <a
                        href={sheetUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={cn(TYPE.tb, "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-medium text-blue-700 ring-1 ring-inset ring-blue-200 hover:bg-blue-50")}
                    >
                        Open the sheet <ExternalLinkIcon size={14} />
                    </a>
                )}
            </div>

            {alertText && (
                <p role="alert" className={cn(TYPE.tb, "m-0 flex items-start gap-2 rounded-2xl bg-amber-50 px-4 py-3 text-amber-900 ring-1 ring-inset ring-amber-200")}>
                    <TriangleAlertIcon size={16} className="mt-0.5 shrink-0" aria-hidden />
                    <span>{alertText}</span>
                </p>
            )}

            {NEEDS_CODE_ON && uncoded.length > 0 && <NeedsCode tasks={uncoded} busyId={busyId} errors={errors} onSave={change} />}

            <div className="flex flex-wrap items-center gap-2">
                <div className="-mx-1 max-w-full overflow-x-auto px-1 py-0.5">
                    <Seg<string>
                        ariaLabel="Phase"
                        value={phase.key}
                        onChange={setPhaseKey}
                        items={phases.map((p) => {
                            const c = phaseCount(p);
                            return { key: p.key, label: `${p.key} · ${c.done}/${c.total}` };
                        })}
                    />
                </div>
                <span className="ml-auto">
                    <Seg<OwnerFilter>
                        ariaLabel="Whose tasks"
                        value={owner}
                        onChange={setOwner}
                        items={[
                            { key: "all", label: "All" },
                            { key: "us", label: "Ours" },
                            { key: "client", label: "Client" },
                        ]}
                    />
                </span>
            </div>

            <div className="rounded-2xl bg-white ring-1 ring-inset ring-black/[0.06]">
                <div className="border-b border-gray-100 px-5 py-4">
                    <p className={cn(TYPE.tt, "m-0 text-gray-900")}>{phase.name}</p>
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-600")}>{phase.sub}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {planLines(phase).map((l) => (
                            <span
                                key={l.code}
                                className={cn(
                                    TYPE.tm,
                                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 ring-1 ring-inset",
                                    l.done === l.total ? "bg-green-50 text-green-700 ring-green-200" : "bg-gray-50 text-gray-700 ring-gray-200",
                                )}
                            >
                                <b className="font-semibold">{l.code}</b> {l.done}/{l.total}
                            </span>
                        ))}
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[860px] border-collapse text-left">
                        <thead>
                            <tr className={cn(TYPE.tm, "text-gray-500")}>
                                <th className="w-10 px-5 py-2 font-medium">#</th>
                                <th className="px-2 py-2 font-medium">Task</th>
                                <th className="w-28 px-2 py-2 font-medium">Owner</th>
                                <th className="w-28 px-2 py-2 font-medium">Target</th>
                                <th className="w-36 px-2 py-2 font-medium">Status</th>
                                <th className="w-[26%] px-2 py-2 pr-5 font-medium">Result record</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((t) => {
                                const codes = codesOf(t.action);
                                const k = ownerKind(t.owner);
                                const changed = changedInSheet[t.id];
                                return (
                                    <tr key={t.id} onClick={() => setOpenId(t.id)} className={cn("cursor-pointer border-t border-gray-100 align-top hover:bg-gray-50", changed && "bg-blue-50/40")}>
                                        <td className={cn(TYPE.tc, "px-5 py-3 tabular-nums text-gray-500")}>{t.n}</td>
                                        <td className="px-2 py-3">
                                            <button type="button" onClick={() => setOpenId(t.id)} className={cn(TYPE.tb, "m-0 block w-full text-left text-gray-900")}>
                                                {codes.map((c) => (
                                                    <span key={c} className="mr-1.5 rounded bg-blue-50 px-1.5 py-0.5 text-xs font-semibold text-blue-700">
                                                        {c}
                                                    </span>
                                                ))}
                                                {actionText(t.action)}
                                            </button>
                                            {changed && <p className={cn(TYPE.tm, "m-0 mt-1 text-blue-700")}>Changed in the sheet · {changed}</p>}
                                            {errors[t.id] && (
                                                <p role="alert" className={cn(TYPE.tm, "m-0 mt-1 text-rose-700")}>
                                                    {errors[t.id]}
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-2 py-3">
                                            <Tag tone={k === "client" ? "orange" : k === "both" ? "violet" : "slate"}>{k === "client" ? "Client" : k === "both" ? "Both" : "Us"}</Tag>
                                        </td>
                                        <td className={cn(TYPE.tc, "whitespace-nowrap px-2 py-3 tabular-nums text-gray-700")}>{t.target}</td>
                                        <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                                            <StatusPick value={t.status} disabled={sample || busyId === t.id} onChange={(s) => void change(t.id, { status: s })} />
                                        </td>
                                        <td className={cn(TYPE.tc, "px-2 py-3 pr-5")}>
                                            {t.result ? <span className="line-clamp-2 text-gray-700">{t.result}</span> : <span className="line-clamp-2 text-gray-400">Done when: {t.gate}</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {onAdd && <AddTask tab={phase.name} key={phase.key} onSave={(body) => addRow(phase, body)} />}

            <Drawer open={Boolean(open)} onClose={() => setOpenId(null)} width={560} itemKey={open?.id}>
                {open && openPhase && (
                    <>
                        <DrawerHead
                            title={actionText(open.action)}
                            status={{
                                tone: STATUS_TONE[open.status] ?? "gray",
                                label: open.status,
                                clock: { text: `Target ${open.target}` },
                            }}
                            chips={
                                <>
                                    {codesOf(open.action).map((c) => (
                                        <Tag key={c} tone="blue">
                                            {c}
                                        </Tag>
                                    ))}
                                    <Tag tone="gray">{openPhase.name}</Tag>
                                    <Tag tone={ownerKind(open.owner) === "client" ? "orange" : "slate"}>{open.owner}</Tag>
                                </>
                            }
                        />
                        <div className="space-y-3">
                            <DrawerBlock label="Status">
                                <StatusPick value={open.status} disabled={sample || busyId === open.id} onChange={(s) => void change(open.id, { status: s })} />
                                {errors[open.id] && (
                                    <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 text-rose-700")}>
                                        {errors[open.id]}
                                    </p>
                                )}
                            </DrawerBlock>
                            {/* The code, edited here now that the Needs a code card is hidden (2 Nov 2026).
                                A plan code (G1) goes at the start of the row in the sheet; anything else is a
                                label kept in Oura. Same rule as that card. */}
                            <DrawerBlock label="Code">
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {(open.codes ?? []).length > 0
                                        ? (open.codes ?? []).map((c) => (
                                              <Tag key={c} tone="blue">
                                                  {c}
                                              </Tag>
                                          ))
                                        : open.label
                                            ? <Tag tone="gray">{open.label}</Tag>
                                            : <span className={cn(TYPE.tb, "text-gray-400")}>No code yet</span>}
                                    {/* A wrong code or label comes off here; typing new codes replaces them too. */}
                                    {onChange && ((open.codes ?? []).length > 0 || open.label) && (
                                        <PillButton
                                            variant="text"
                                            size="sm"
                                            busy={busyId === open.id}
                                            disabled={busyId !== null}
                                            onClick={() => void change(open.id, (open.codes ?? []).length > 0 ? { code: "" } : { label: null }, `${open.id}:code`)}
                                        >
                                            Clear
                                        </PillButton>
                                    )}
                                </div>
                                {onChange && (
                                    <div className="mt-2 flex flex-wrap items-center gap-2">
                                        <input
                                            aria-label={`Code or label for ${open.action}`}
                                            className={cn(TYPE.tb, "w-40 rounded-xl border border-gray-200 px-3 py-1.5 text-gray-900 focus:border-blue-400 focus:outline-none")}
                                            placeholder="G1, G2, or a label"
                                            value={codeDraft.id === open.id ? codeDraft.text : ""}
                                            disabled={busyId === open.id}
                                            onChange={(e) => setCodeDraft({ id: open.id, text: e.target.value })}
                                        />
                                        {(() => {
                                            const v = codeDraft.id === open.id ? codeDraft.text.trim() : "";
                                            const isCode = CODES_RE.test(v);
                                            return (
                                                <PillButton
                                                    size="sm"
                                                    busy={busyId === open.id}
                                                    disabled={!v || busyId !== null}
                                                    onClick={async () => {
                                                        const id = open.id;
                                                        if (await change(id, isCode ? { code: v.toUpperCase() } : { label: v }, `${id}:code`)) {
                                                            setCodeDraft((d) => (d.id === id ? { id, text: "" } : d));
                                                        }
                                                    }}
                                                >
                                                    {isCode ? "Set code" : "Save label"}
                                                </PillButton>
                                            );
                                        })()}
                                    </div>
                                )}
                                {errors[`${open.id}:code`] && (
                                    <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 text-rose-700")}>
                                        {errors[`${open.id}:code`]}
                                    </p>
                                )}
                            </DrawerBlock>
                            <DrawerBlock label="Target">
                                <input
                                    type="date"
                                    aria-label="Target date"
                                    className={cn(TYPE.tb, "rounded-xl border border-gray-200 px-3 py-1.5 text-gray-900 focus:border-blue-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500")}
                                    value={isoDay(open.target) ?? ""}
                                    disabled={sample || busyId === open.id}
                                    onChange={(e) => {
                                        // The sheet's Target takes a date, not a blank: a cleared field keeps the date it had.
                                        if (e.target.value && e.target.value !== isoDay(open.target)) void change(open.id, { target: e.target.value }, `${open.id}:target`);
                                    }}
                                />
                                {open.target && !isoDay(open.target) && (
                                    <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>
                                        Now: {open.target}, as text in the sheet. Picking a date replaces it.
                                    </p>
                                )}
                                {errors[`${open.id}:target`] && (
                                    <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 text-rose-700")}>
                                        {errors[`${open.id}:target`]}
                                    </p>
                                )}
                            </DrawerBlock>
                            <DrawerBlock label="Done when">{open.gate || "Not set"}</DrawerBlock>
                            <DrawerBlock label="Result record">
                                {onChange && rewrite !== null ? (
                                    // A rewrite of the whole record (#4815): written as typed, no date stamp.
                                    <>
                                        <label htmlFor={`result-edit-${open.id}`} className={cn(TYPE.tm, "block text-gray-600")}>
                                            The whole record, as it should read
                                        </label>
                                        <textarea
                                            id={`result-edit-${open.id}`}
                                            className={cn(TYPE.tb, "mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-gray-900 focus:border-blue-400 focus:outline-none")}
                                            rows={5}
                                            maxLength={RESULT_MAX}
                                            value={rewrite}
                                            onChange={(e) => {
                                                setRewrite(e.target.value);
                                                setConfirmClear(false);
                                            }}
                                        />
                                        {confirmClear ? (
                                            <div className="mt-2 space-y-2">
                                                <p className={cn(TYPE.tb, "m-0 text-gray-900")}>Clear the result record? It is cleared in the sheet too.</p>
                                                <div className="flex flex-wrap gap-2">
                                                    <PillButton
                                                        variant="secondary"
                                                        className="text-rose-700"
                                                        busy={busyId === open.id}
                                                        disabled={busyId !== null}
                                                        onClick={async () => {
                                                            if (await change(open.id, { result: "" }, `${open.id}:result`)) setRewrite(null);
                                                            setConfirmClear(false);
                                                        }}
                                                    >
                                                        Yes, clear it
                                                    </PillButton>
                                                    <PillButton disabled={busyId !== null} onClick={() => setConfirmClear(false)}>
                                                        Keep it
                                                    </PillButton>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                <PillButton
                                                    variant="secondary"
                                                    busy={busyId === open.id}
                                                    disabled={busyId !== null || rewrite.trim() === (open.result ?? "").trim()}
                                                    onClick={async () => {
                                                        const text = rewrite.trim();
                                                        // Clearing a record that has text asks first.
                                                        if (!text && (open.result ?? "").trim()) {
                                                            setConfirmClear(true);
                                                            return;
                                                        }
                                                        if (await change(open.id, { result: text }, `${open.id}:result`)) setRewrite(null);
                                                    }}
                                                >
                                                    Save the record
                                                </PillButton>
                                                <PillButton
                                                    disabled={busyId !== null}
                                                    onClick={() => {
                                                        setRewrite(null);
                                                        setErrors((e) => ({ ...e, [`${open.id}:result`]: "" }));
                                                    }}
                                                >
                                                    Cancel
                                                </PillButton>
                                            </div>
                                        )}
                                    </>
                                ) : onChange ? (
                                    <>
                                        <div className="flex items-start justify-between gap-3">
                                            <p className={cn(TYPE.tb, "m-0 min-w-0 whitespace-pre-wrap", open.result ? "text-gray-900" : "text-gray-400")}>{open.result || "Nothing recorded yet."}</p>
                                            <PillButton
                                                icon={PencilIcon}
                                                className="flex-shrink-0"
                                                disabled={busyId !== null}
                                                onClick={() => {
                                                    setRewrite(open.result ?? "");
                                                    setErrors((e) => ({ ...e, [`${open.id}:result`]: "" }));
                                                }}
                                            >
                                                Edit
                                            </PillButton>
                                        </div>
                                        <label htmlFor={`result-${open.id}`} className={cn(TYPE.tm, "mt-3 block text-gray-600")}>
                                            Add a line (dated, under what is there)
                                        </label>
                                        <textarea
                                            id={`result-${open.id}`}
                                            className={cn(TYPE.tb, "mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-gray-900 focus:border-blue-400 focus:outline-none")}
                                            rows={3}
                                            value={append}
                                            placeholder="What was done, with a link."
                                            onChange={(e) => setAppend(e.target.value)}
                                        />
                                        <PillButton
                                            className="mt-2"
                                            busy={busyId === open.id}
                                            disabled={!append.trim()}
                                            onClick={async () => {
                                                if (await change(open.id, { resultAppend: append.trim() }, `${open.id}:result`)) setAppend("");
                                            }}
                                        >
                                            Add to the record
                                        </PillButton>
                                    </>
                                ) : (
                                    <textarea
                                        id={`result-${open.id}`}
                                        className={cn(TYPE.tb, "mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-gray-900 focus:border-blue-400 focus:outline-none disabled:bg-gray-50 disabled:text-gray-500")}
                                        rows={5}
                                        disabled={sample}
                                        value={open.result}
                                        placeholder="What was done, with the date and a link."
                                        onChange={(e) => update(open.id, { result: e.target.value })}
                                    />
                                )}
                                {errors[`${open.id}:result`] && (
                                    <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 text-rose-700")}>
                                        {errors[`${open.id}:result`]}
                                    </p>
                                )}
                            </DrawerBlock>
                            {open.notes && <DrawerBlock label="Notes">{open.notes}</DrawerBlock>}
                            <DrawerBlock label="Tool">{open.tool || "None"}</DrawerBlock>
                            <DrawerBlock label="History">
                                {open.history ? (
                                    <HistoryList entries={open.history} />
                                ) : (
                                    <ul className={cn(TYPE.tc, "m-0 list-none space-y-1 p-0 text-gray-600")}>
                                        {changedInSheet[open.id] && (
                                            <li>
                                                Status set to {open.status} in the sheet · {changedInSheet[open.id]}
                                            </li>
                                        )}
                                        <li>Made from the Execution Timeline</li>
                                    </ul>
                                )}
                            </DrawerBlock>
                            {onDelete && (
                                <div className="border-t border-gray-100 pt-3">
                                    {confirmDelete ? (
                                        <div className="space-y-2">
                                            <p className={cn(TYPE.tb, "m-0 text-gray-900")}>Delete this task? It is removed from the client's tracker sheet too.</p>
                                            <div className="flex flex-wrap gap-2">
                                                <PillButton variant="secondary" className="text-rose-700" busy={busyId === open.id} disabled={busyId !== null} onClick={() => void remove(open.id)}>
                                                    Yes, delete it
                                                </PillButton>
                                                <PillButton disabled={busyId !== null} onClick={() => setConfirmDelete(false)}>
                                                    Keep it
                                                </PillButton>
                                            </div>
                                        </div>
                                    ) : (
                                        <PillButton icon={Trash2Icon} className="text-rose-700" disabled={busyId !== null} onClick={() => setConfirmDelete(true)}>
                                            Delete this task
                                        </PillButton>
                                    )}
                                    {errors[`${open.id}:delete`] && (
                                        <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 text-rose-700")}>
                                            {errors[`${open.id}:delete`]}
                                        </p>
                                    )}
                                </div>
                            )}
                            <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                                {sample ? "Sample only. Changes are not saved." : `Saves to Oura and to row ${open.n} of the ${openPhase.name} tab.`}
                            </p>
                        </div>
                    </>
                )}
            </Drawer>
        </div>
    );
}

const FIELD_CLS = cn(TYPE.tb, "w-full rounded-xl border border-gray-200 px-3 py-1.5 text-gray-900 focus:border-blue-400 focus:outline-none disabled:bg-gray-50");

/**
 * "Add a task" under a phase: a small form. The row is added only when the server answers 201.
 * Each new task carries an idempotencyKey: the same key sent again returns the task it already made,
 * so a retry after an error never adds a second row. The key is kept across retries and made new only
 * after a task is added, so a changed retry of an ambiguous add still lands on the same task.
 */
function AddTask({ tab, onSave }: { tab: string; onSave: (body: Omit<NewTask, "tab">) => Promise<AddAnswer> }) {
    const [open, setOpen] = useState(false);
    const [action, setAction] = useState("");
    const [owner, setOwner] = useState("ourteam");
    const [target, setTarget] = useState("");
    const [status, setStatus] = useState<string>("Not started");
    const [attempt, setAttempt] = useState(() => crypto.randomUUID());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<{ field?: string; message: string } | null>(null);

    const save = async () => {
        setBusy(true);
        setError(null);
        const ans = await onSave({ action: action.trim(), owner, status, ...(target ? { target } : {}), idempotencyKey: attempt });
        setBusy(false);
        if (!ans.ok) {
            setError({ field: ans.field, message: ans.message });
            return;
        }
        setAttempt(crypto.randomUUID());
        setAction("");
        setOwner("ourteam");
        setTarget("");
        setStatus("Not started");
        setOpen(false);
    };

    if (!open) {
        return (
            <PillButton className="mt-3" icon={PlusIcon} onClick={() => setOpen(true)}>
                Add a task
            </PillButton>
        );
    }
    const fieldError = (field: string) =>
        error?.field === field ? (
            <p role="alert" className={cn(TYPE.tm, "m-0 mt-1 text-rose-700")}>
                {error.message}
            </p>
        ) : null;
    return (
        <form
            className="mt-3 rounded-2xl bg-white px-5 py-4 ring-1 ring-inset ring-black/[0.06]"
            onSubmit={(e) => {
                e.preventDefault();
                if (action.trim() && !busy) void save();
            }}
        >
            <p className={cn(TYPE.tt, "m-0 text-gray-900")}>New task in {tab}</p>
            <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-600")}>It is added as the last row of this tab, in the client's sheet and in Oura.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1 sm:col-span-2">
                    <span className={cn(TYPE.tm, "text-gray-600")}>Task</span>
                    <input aria-label="Task" className={FIELD_CLS} value={action} disabled={busy} onChange={(e) => setAction(e.target.value)} />
                    {fieldError("action")}
                </label>
                <label className="flex flex-col gap-1">
                    <span className={cn(TYPE.tm, "text-gray-600")}>Owner</span>
                    <select aria-label="Owner" className={FIELD_CLS} value={owner} disabled={busy} onChange={(e) => setOwner(e.target.value)}>
                        {OWNERS.map((o) => (
                            <option key={o} value={o}>
                                {o}
                            </option>
                        ))}
                    </select>
                    {fieldError("owner")}
                </label>
                <label className="flex flex-col gap-1">
                    <span className={cn(TYPE.tm, "text-gray-600")}>Target</span>
                    <input type="date" aria-label="Target" className={FIELD_CLS} value={target} disabled={busy} onChange={(e) => setTarget(e.target.value)} />
                    {fieldError("target")}
                </label>
                <label className="flex flex-col gap-1">
                    <span className={cn(TYPE.tm, "text-gray-600")}>Status</span>
                    <select aria-label="Status" className={FIELD_CLS} value={status} disabled={busy} onChange={(e) => setStatus(e.target.value)}>
                        {TASK_STATUSES.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                    {fieldError("status")}
                </label>
            </div>
            {error && !error.field && (
                <p role="alert" className={cn(TYPE.tc, "m-0 mt-3 text-rose-700")}>
                    {error.message}
                </p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
                <PillButton variant="primary" type="submit" busy={busy} disabled={!action.trim()}>
                    Add the task
                </PillButton>
                <PillButton disabled={busy} onClick={() => setOpen(false)}>
                    Cancel
                </PillButton>
            </div>
        </form>
    );
}

function StatusPick({ value, onChange, disabled = false }: { value: string; onChange: (s: string) => void; disabled?: boolean }) {
    const tone = STATUS_TONE[value] ?? "gray";
    const cls: Record<string, string> = {
        gray: "bg-gray-50 text-gray-700 ring-gray-200",
        blue: "bg-blue-50 text-blue-700 ring-blue-200",
        rose: "bg-rose-50 text-rose-700 ring-rose-200",
        amber: "bg-amber-50 text-amber-800 ring-amber-200",
        green: "bg-green-50 text-green-700 ring-green-200",
    };
    return (
        <select
            aria-label="Status"
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            className={cn(TYPE.tc, "rounded-full py-1 pl-2.5 pr-7 font-medium ring-1 ring-inset disabled:cursor-not-allowed disabled:opacity-60", cls[tone] ?? cls.gray)}
        >
            {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>
                    {s}
                </option>
            ))}
        </select>
    );
}

function HistoryList({ entries }: { entries: TaskHistory[] }) {
    if (!entries.length) return <p className={cn(TYPE.tc, "m-0 text-gray-500")}>No changes since Oura first read this row.</p>;
    return (
        <ul className={cn(TYPE.tc, "m-0 list-none space-y-2 p-0 text-gray-700")}>
            {entries.map((h, i) => (
                <li key={`${h.at}-${h.field}-${i}`}>
                    <span className="font-medium">{FIELD_WORD[h.field] ?? h.field}</span>
                    {h.after !== null && h.after !== "" ? `: ${h.after}` : ""}
                    <span className="text-gray-500">
                        {" "}
                        · {h.where === "sheet" ? "in the sheet" : "in Oura"}
                        {h.by ? `, ${h.by}` : ""}
                        {agoText(h.at) ? `, ${agoText(h.at)}` : ""}
                    </span>
                    {h.note && <span className="block text-amber-800">{h.note}</span>}
                </li>
            ))}
        </ul>
    );
}

/** Rows that start with no plan code: staff give one (written into the sheet's Action) or a free label (kept in Oura). */
function NeedsCode({ tasks, busyId, errors, onSave }: { tasks: Task[]; busyId: string | null; errors: Record<string, string>; onSave: (id: string, ch: TaskChange) => Promise<boolean> }) {
    const [values, setValues] = useState<Record<string, string>>({});
    return (
        <div className="rounded-2xl bg-white px-5 py-4 ring-1 ring-inset ring-black/[0.06]">
            <p className={cn(TYPE.tt, "m-0 text-gray-900")}>Needs a code · {tasks.length}</p>
            <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-600")}>Type a plan code (G1, S3) to write it at the start of the row in the sheet, or a label to keep in Oura.</p>
            <ul className="m-0 mt-3 flex list-none flex-col p-0">
                {tasks.map((t) => {
                    const v = (values[t.id] ?? "").trim();
                    const isCode = CODES_RE.test(v);
                    return (
                        <li key={t.id} className="flex flex-col gap-2 border-t border-gray-100 py-2 first:border-t-0 sm:flex-row sm:items-center">
                            <p className={cn(TYPE.tb, "m-0 min-w-0 flex-1 text-gray-900")}>{t.action}</p>
                            <input
                                aria-label={`Code or label for ${t.action}`}
                                className={cn(TYPE.tb, "w-full rounded-xl border border-gray-200 px-3 py-1.5 sm:w-48 focus:border-blue-400 focus:outline-none")}
                                placeholder="G1, G2, or a label"
                                value={values[t.id] ?? ""}
                                onChange={(e) => setValues((s) => ({ ...s, [t.id]: e.target.value }))}
                            />
                            <PillButton size="sm" busy={busyId === t.id} disabled={!v} onClick={() => void onSave(t.id, isCode ? { code: v.toUpperCase() } : { label: v })}>
                                {isCode ? "Set code" : "Save label"}
                            </PillButton>
                            {errors[t.id] && (
                                <p role="alert" className={cn(TYPE.tm, "m-0 text-rose-700 sm:basis-full")}>
                                    {errors[t.id]}
                                </p>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
